/**
 * Stale-bundle guard (ENG-3523).
 *
 * Studio code-splits its routes; a tab opened before a deploy still holds the
 * previous bundle, and the first lazy route it visits after the deploy asks for
 * a chunk by a hashed filename that no longer exists. Vite reports that as a
 * `vite:preloadError` event on `window`; left alone, the failed import unmounts
 * the whole React tree to a blank page while the URL has already changed.
 *
 * The guard reloads the page once so it picks up the current build. A marker in
 * sessionStorage, keyed by the URL that failed, makes it one-shot: if the same
 * URL fails again right after the reload (the build is genuinely broken, not
 * stale), the error is left to surface instead of looping.
 */
export const STALE_BUNDLE_RELOAD_KEY = "swivl:stale-bundle-reloaded-for";

export interface StaleBundleGuardTarget {
  addEventListener: (type: string, listener: (event: Event) => void) => void;
  location: { href: string; reload: () => void };
  sessionStorage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

export function installStaleBundleGuard(target: StaleBundleGuardTarget): void {
  target.addEventListener("vite:preloadError", (event: Event) => {
    const href = target.location.href;
    let alreadyReloaded = false;
    try {
      alreadyReloaded = target.sessionStorage.getItem(STALE_BUNDLE_RELOAD_KEY) === href;
    } catch {
      // Storage unavailable (private window, blocked site data): fall through to a single reload.
    }
    if (alreadyReloaded) {
      // Second failure on the same URL after a reload — not a stale tab. Let it surface.
      try {
        target.sessionStorage.removeItem(STALE_BUNDLE_RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      target.sessionStorage.setItem(STALE_BUNDLE_RELOAD_KEY, href);
    } catch {
      /* ignore */
    }
    // Vite would otherwise rethrow the import error; we are handling it with a reload.
    event.preventDefault();
    target.location.reload();
  });
}
